import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Globe, Lock, Settings, Shield, X, CheckCircle } from 'lucide-react';
import { baseUrl } from '../config';
import SEO from '../components/SEO';



// Base Components with Enhanced Styling
const Card = ({ children, className = '' }) => (
    <div className={`bg-white rounded-lg shadow-md border border-teal-200 transition-transform ${className}`}>
        {children}
    </div>
);

const CardContent = ({ children, className = '' }) => (
    <div className={`p-6 ${className}`}>{children}</div>
);

const Alert = ({ children, className = '' }) => (
    <div className={`bg-teal-50 border-l-4 border-teal-400 p-4 ${className}`}>
        {children}
    </div>
);

const AlertDescription = ({ children, className = '' }) => (
    <p className={`text-sm ${className}`}>{children}</p>
);

const Button = ({ children, onClick, variant = 'default', size = 'default', className = '', ...props }) => {
    const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 disabled:pointer-events-none disabled:opacity-50';

    const variants = {
        default: 'bg-teal-600 text-white hover:bg-teal-700',
        outline: 'border border-teal-200 bg-white text-teal-700 hover:bg-teal-100',
        ghost: 'hover:bg-teal-100 hover:text-teal-700',
    };

    const sizes = {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-sm',
        icon: 'h-9 w-9',
    };

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
            onClick={onClick}
            {...props}
        >
            {children}
        </button>
    );
};

const sections = [
    { id: 'overview', label: 'Overview', icon: Globe },
    { id: 'essential', label: 'Essential Cookies', icon: Lock, tooltip: 'Required for website functionality' },
    { id: 'analytics', label: 'Analytics Cookies', icon: Settings, tooltip: 'Helps us understand site usage' },
    { id: 'advertising', label: 'Advertising Cookies', icon: Shield, tooltip: 'Personalized ads and content' },
    { id: 'preferences', label: 'Preferences', icon: Globe, tooltip: 'Store user preferences and settings' },
];

const CookiePolicy = () => {
    const [showBanner, setShowBanner] = useState(true);
    const [activeSection, setActiveSection] = useState('overview');
    const [cookiePreferences, setCookiePreferences] = useState({
        essential: true,
        analytics: false,
        advertising: false,
        preferences: false,
    });
    const [expandedSections, setExpandedSections] = useState({});
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const togglePreference = (type) => {
        setCookiePreferences((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const toggleSection = (sectionId) => {
        setExpandedSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    };

    const savePreferences = () => {
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
    };

    const CookieBanner = () => (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-teal-200 p-4 z-50">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-teal-600">
                    We use cookies to enhance your experience. By continuing to visit this site, you agree to our use of cookies.
                    <a href="#learn-more" className="text-teal-600 hover:underline ml-1">Learn More</a>
                </p>
                <div className="flex gap-3">
                    <Button onClick={() => setShowBanner(false)} variant="outline">Decline Optional</Button>
                    <Button onClick={savePreferences}>Accept All</Button>
                    <Button onClick={() => setShowBanner(false)} variant="ghost" size="icon" className="text-teal-400 hover:text-teal-500">
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );

    const Header = () => (
        <header className="bg-white border-b border-teal-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16 items-center">
                    <h1 className="text-2xl font-semibold text-teal-900">Cookie Policy</h1>
                </div>
            </div>
        </header>
    );

    const Navigation = () => (
        <nav className="col-span-12 sm:col-span-3">
            <div className="space-y-1">
                {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-all duration-300 ${
                                activeSection === section.id ? 'bg-teal-50 text-teal-700' : 'text-teal-700 hover:bg-teal-50'
                            }`}
                        >
                            <Icon className="h-5 w-5" />
                            <span className="text-sm font-medium">{section.label}</span>
                            {section.tooltip && (
                                <span className="text-xs bg-teal-200 rounded-md px-2 py-1 ml-auto">{section.tooltip}</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </nav>
    );

    const ContentSection = () => (
        <Card>
            <CardContent>
                {activeSection === 'overview' ? (
                    <div className="space-y-6">
                        <Alert>
                            <AlertDescription className="text-sm text-teal-700">
                                Last updated: November 3, 2024
                            </AlertDescription>
                        </Alert>
                        <div className="prose max-w-none">
                            <h2 className="text-xl font-semibold text-teal-900 mb-4">About Our Cookie Policy</h2>
                            <p className="text-teal-600 mb-4">
                                We use cookies and similar technologies to provide, protect, and improve our products and services.
                                This policy explains how and why we use these technologies and the choices you have.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-teal-900 mb-4">
                            {sections.find((s) => s.id === activeSection)?.label}
                        </h2>
                        <div className="flex justify-between items-center">
                            <span className="text-teal-700 font-medium">Enable {sections.find((s) => s.id === activeSection)?.label}</span>
                            <input
                                type="checkbox"
                                checked={cookiePreferences[activeSection]}
                                onChange={() => togglePreference(activeSection)}
                                className="w-5 h-5 text-teal-600 rounded focus:ring-0 transition-all"
                            />
                        </div>
                        <Button onClick={() => toggleSection(activeSection)} className="mt-4">
                            {expandedSections[activeSection] ? 'Collapse Details' : 'Expand Details'}
                            {expandedSections[activeSection] ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
                        </Button>
                        {expandedSections[activeSection] && (
                            <div className="mt-4 text-teal-600">
                                <p>Details about {sections.find((s) => s.id === activeSection)?.label} go here.</p>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );

    const FAQSection = () => (
        <Card className="mt-8">
            <CardContent>
                <h2 className="text-xl font-semibold text-teal-900 mb-4">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold text-teal-800">What are cookies?</h3>
                        <p className="text-teal-600">Cookies are small text files stored on your device to help improve your browsing experience.</p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-teal-800">How do I manage cookies?</h3>
                        <p className="text-teal-600">You can manage cookies through your browser settings or the cookie management tool on our site.</p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-teal-800">Will I still be able to use the site if I disable cookies?</h3>
                        <p className="text-teal-600">Yes, but some features may not work properly without cookies enabled.</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="bg-gray-50 min-h-screen">
            <SEO
              title="Cookie Policy"
              description="Read ProducerBuddy's cookie policy to understand how we use cookies and protect your privacy on our film production platform."
              url="https://producerbuddy.pilomation.com/cookies"
            />
            {showBanner && <CookieBanner />}
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-12 gap-6">
                    <Navigation />
                    <div className="col-span-12 sm:col-span-9">
                        <ContentSection />
                        {showSuccessMessage && (
                            <div className="mt-4">
                                <Alert className="flex items-center">
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                    <span className="ml-2 text-green-700">Your preferences have been saved!</span>
                                </Alert>
                            </div>
                        )}
                    </div>
                </div>
                <FAQSection />
            </main>
        </div>
    );
};

export default CookiePolicy;

